import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Box,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const ConfirmContext = createContext(() => Promise.resolve(false));

/**
 * Provides an imperative `confirm(options)` that resolves to true/false. Use for destructive or
 * costly actions:  `const ok = await confirm({ title, description, confirmColor: 'error' })`.
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {} });
  const resolver = useRef(null);

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setState({ open: true, options });
      }),
    [],
  );

  const settle = (result) => {
    resolver.current?.(result);
    resolver.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  const { open, options } = state;
  const {
    title = 'Are you sure?',
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmColor = 'primary',
    icon = <WarningAmberRoundedIcon />,
  } = options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onClose={() => settle(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: `${confirmColor}.main`,
                bgcolor: (t) => `${t.palette[confirmColor]?.main || t.palette.primary.main}14`,
              }}
            >
              {icon}
            </Box>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {title}
            </Box>
          </Stack>
        </DialogTitle>
        {description && (
          <DialogContent>
            <DialogContentText component="div">{description}</DialogContentText>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => settle(false)} color="inherit">
            {cancelText}
          </Button>
          <Button onClick={() => settle(true)} variant="contained" color={confirmColor} autoFocus>
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/** Hook returning the imperative confirm() function. */
export const useConfirm = () => useContext(ConfirmContext);

export default ConfirmProvider;
